import {
  Box,
  Button,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SessionContext from "../layout/SessionContext";
import { ClubMode } from "./clubApi";
import useClubActions from "./useClubActions";
import useUserClubs from "./useUserClubs";

export const ALL_CLUBS = -1;

export const MODE_PAGES: Record<ClubMode, { home: string; paths: string[] }> = {
  manager: { home: "/club", paths: ["/club", "/game", "/player", "/horses"] },
  player: {
    home: "/playerclubselection",
    paths: ["/playerclubselection", "/playergame", "/playerclub", "/playerhorses"],
  },
};

type ClubSwitcherProps = {
  mode: ClubMode;
  devPort: string;
  placement?: "toolbar" | "strip";
};

function ClubSwitcher({ mode, devPort, placement = "toolbar" }: ClubSwitcherProps) {
  const { userName, clubId, setClubId, selectClubManually } = useContext(SessionContext);
  const { state } = useUserClubs(devPort, userName, mode);
  const { openJoin, openRegister, dialogs } = useClubActions(mode, devPort);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const clubs = state.status === "ready" ? state.clubs : [];
  const selectedClub = clubs.find((club) => club.value === clubId);
  const canShowAllClubs = mode === "player" && clubs.length > 1;

  // Managers always work inside one of their clubs; players with a single club
  // have it selected, and players with several default to "All clubs".
  useEffect(() => {
    if (state.status !== "ready" || state.clubs.length === 0) {
      return;
    }
    const isKnownClub = state.clubs.some((club) => club.value === clubId);
    if (mode === "manager" && !isKnownClub) {
      setClubId(state.clubs[0].value);
    } else if (mode === "player" && state.clubs.length === 1 && !isKnownClub) {
      setClubId(state.clubs[0].value);
    } else if (mode === "player" && clubId !== ALL_CLUBS && !isKnownClub) {
      setClubId(ALL_CLUBS);
    }
  }, [state, clubId, mode, setClubId]);

  function selectClub(value: number) {
    setAnchorEl(null);
    selectClubManually(value);
    if (!MODE_PAGES[mode].paths.includes(window.location.pathname)) {
      navigate(MODE_PAGES[mode].home);
    }
  }

  if (state.status !== "ready" || (clubs.length === 0 && mode === "manager")) {
    return null;
  }

  const label = selectedClub
    ? selectedClub.label
    : mode === "player"
    ? "All clubs"
    : "Select a club";

  const openMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  return (
    <>
      {placement === "strip" ? (
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <ButtonBase
            onClick={openMenu}
            data-testid="club-switcher"
            data-placement="strip"
            aria-haspopup="menu"
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 3,
              textAlign: "left",
              color: "white",
              bgcolor: "secondary.main",
              boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18)",
              transition: "background-color 0.2s",
              "&:hover, &:focus-visible": { bgcolor: "secondary.dark" },
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.16)",
              }}
            >
              <GroupsOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                component="div"
                sx={{
                  opacity: 0.75,
                  lineHeight: 1.2,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: 10,
                }}
              >
                {mode === "manager" ? "Managing" : "Viewing"}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            </Box>
            <UnfoldMoreIcon sx={{ opacity: 0.85 }} />
          </ButtonBase>
        </Box>
      ) : (
        <Button
          variant="contained"
          color="secondary"
          disableElevation
          onClick={openMenu}
          endIcon={<ArrowDropDownIcon />}
          data-testid="club-switcher"
          data-placement="toolbar"
          aria-haspopup="menu"
          sx={{
            mx: 1.5,
            maxWidth: 260,
            borderRadius: 999,
            px: 2,
            fontWeight: 600,
            "& .MuiButton-endIcon": { ml: 0.5 },
          }}
        >
          <span
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {label}
          </span>
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 3,
              width: placement === "strip" ? anchorEl?.offsetWidth : undefined,
            },
          },
        }}
      >
        {canShowAllClubs && (
          <MenuItem
            selected={!selectedClub}
            onClick={() => selectClub(ALL_CLUBS)}
            data-testid="club-switcher-all"
          >
            <ListItemIcon>{!selectedClub && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>All clubs</ListItemText>
          </MenuItem>
        )}
        {clubs.map((club) => (
          <MenuItem
            key={club.value}
            selected={club.value === clubId}
            onClick={() => selectClub(club.value)}
            data-testid={"club-switcher-" + club.value}
          >
            <ListItemIcon>
              {club.value === clubId && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{club.label}</ListItemText>
          </MenuItem>
        ))}
        {clubs.length > 0 && <Divider />}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (mode === "manager") {
              openRegister();
            } else {
              openJoin();
            }
          }}
          data-testid="club-switcher-add"
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {mode === "manager" ? "Register a new club" : "Join a club"}
          </ListItemText>
        </MenuItem>
      </Menu>
      {dialogs}
    </>
  );
}

export default ClubSwitcher;
